/* eslint-disable @typescript-eslint/no-namespace */
import type { stacks } from "@dropbox/api-v2-client";
import type { DemoPromptSuggestion } from "@mirage/growth/onboarding/hooks/useDemoQuerySuggestions";
import type { ConnectorConnections } from "@mirage/service-connectors/service";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type { PreConfiguredTemplateID } from "@mirage/shared/compose/assist-template";
import type { PreConfiguredVoiceID } from "@mirage/shared/compose/compose-voice";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import type { PeopleRecommendation } from "@mirage/shared/search/people-recommendations";
import type { RecentChat } from "@mirage/shared/search/recent-chats";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import type {
  SearchFilter,
  SearchFilterBindingStr,
} from "@mirage/shared/search/search-filters";
import type { SearchResult } from "@mirage/shared/search/search-result";
import type { StackItem } from "@mirage/shared/search/stack-item";
import type { URLShortcut } from "@mirage/shared/search/url-shortcut";
import type { Observable } from "rxjs";
import type { TypeaheadCache } from "../typeahead-cache";
import type { HitTracking } from "./common";

export namespace typeahead {
  export type Config = {
    isDropboxer: boolean;
    attachmentUploadFilenames?: string[];
    initialQuery?: string;
    isDualModeLauncher?: boolean;

    // filters
    isTypeaheadFiltersEnabled: boolean;
    isOmniboxEnabled: boolean;
    isPeopleFilterRowEnabled: boolean;
    isPeopleRecommendationEnabled: boolean;
    isSummarizableRecommendationsOnly?: boolean;

    // Other behavior
    isNullStateUsesRecommendations: boolean;
    connections: ConnectorConnections;
    allFiltersSet: Set<SearchFilterBindingStr>;
    recentChats: RecentChat[];
    // Improvement note (AYC): ideally better to have a calendar-specific typeahead type, but we're re-using the existing type
    calendarEvents: SearchResult[];
    isIncreasedDesktopApplicationScoring: boolean;
    demoSuggestions: DemoPromptSuggestion[];
    prioritizeFindInNullStateExp: "TEST" | "CONTROL" | "OFF";
  } & PerInstanceConfig;

  export enum Mode {
    Default = "default",
    Find = "find",
    Ask = "ask",
    Write = "write",
    Organize = "organize",
  }

  export type PerInstanceConfig = {
    isRecentQueriesEnabled: boolean;

    // this may override the global isNullStateUsesRecommendations if specified
    isNullStateUsesRecommendations?: boolean;
  };

  export type CacheAnalysis = {
    hasRecentQueries: boolean;
  };

  export enum SourceId {
    // The "suggested query" reflecting user typing in "Find/Ask" sections
    SuggestedQueries = "suggested-queries",

    // Classic Typeahead sources
    RecentQueries = "recent-queries",
    Recommendations = "recommendations",
    URLShortcuts = "url-shortcuts",
    Stacks = "stacks",
    StackItems = "stack-items",
    MathCalculations = "math-calculations",
    SearchFilter = "search-filters",
    MyProfileSuggestions = "my-profile-suggestions",
    CalendarEvents = "calendar-events",

    // Desktop only
    DesktopApplications = "desktop-applications",
    RecentFiles = "recent-files",

    // Omnibox Specific
    AskSuggestions = "ask-suggestions",
    RecentChats = "recent-chats",
    DraftWithSource = "draft-with-source",
    DraftFromTemplate = "draft-from-template",
    DraftFromTone = "draft-from-tone",
    PeopleFilters = "people-filters",
    PeopleRecommendations = "people-recommendations",
  }

  export enum SectionId {
    Find = "find",
    Recommended = "recommended",
    Recent = "recent",
    AskDash = "ask-dash",
    Ask = "ask",
    Organize = "organize",
    Suggested = "suggested",
    Create = "create",
    DraftFromTemplate = "draft-from-template",
    DraftFromTone = "draft-from-tone",
    DraftFromSource = "draft-from-source",
  }

  export enum ResultType {
    Stack = "stack",
    StackItem = "stack-item",
    SearchResult = "search-result",
    PreviousQuery = "previous-query",
    SuggestedQuery = "suggested-query",
    URLShortcut = "url-shortcut",
    Recommendation = "recommendation",
    MathCalculation = "math-calculation",
    SearchFilter = "search-filter",
    CalendarEvent = "calendar-event",

    // native results
    DesktopFile = "desktop-file",
    DesktopApplication = "desktop-application",

    // Omnibox Specific
    AskSuggestion = "answer-suggestion",
    AskUserQuery = "ask-user-query",
    RecentChat = "recent-chat",
    OrganizeUserQuery = "organize-user-query",
    DraftWithSource = "draft-with-source",
    DraftFromTemplate = "draft-from-template",
    DraftFromTone = "draft-from-tone",
    WriteUserQuery = "write-user-query",
    PeopleFilter = "people-filter",
    PeopleRecommendation = "people-recommendation",
    MyProfileSuggestion = "my-profile-suggestion",
  }

  export type PrimitiveMap = {
    [ResultType.Stack]: stacks.Stack;
    [ResultType.StackItem]: StackItem;
    [ResultType.SearchResult]: SearchResult;
    [ResultType.PreviousQuery]: PreviousQuery;
    [ResultType.SuggestedQuery]: SuggestedQuery;
    [ResultType.URLShortcut]: URLShortcut;
    [ResultType.Recommendation]: Recommendation;
    [ResultType.MathCalculation]: MathCalculation;
    [ResultType.SearchFilter]: SearchFilter;
    // Improvement note (AYC): ideally have a calendar-specific typeahead type, but we're re-using the existing type
    [ResultType.CalendarEvent]: SearchResult;

    // native results
    [ResultType.DesktopFile]: SearchResult;
    [ResultType.DesktopApplication]: SearchResult;

    // Omnibox Specific
    [ResultType.AskSuggestion]: QuerySuggestionResult;
    [ResultType.AskUserQuery]: AskUserQuery;
    [ResultType.RecentChat]: RecentChat;
    [ResultType.OrganizeUserQuery]: OrganizeUserQuery;
    // We're mapping to recommendations and filtering out to docs
    [ResultType.DraftWithSource]: Recommendation;
    [ResultType.DraftFromTemplate]: DraftFromTemplateResult;
    [ResultType.DraftFromTone]: DraftFromToneResult;
    [ResultType.WriteUserQuery]: WriteUserQuery;
    [ResultType.PeopleFilter]: PeopleRecommendation;
    [ResultType.PeopleRecommendation]: PeopleRecommendation;
    [ResultType.MyProfileSuggestion]: PeopleRecommendation;
  };

  export type TypeToPrimitive<T extends ResultType> = PrimitiveMap[T];

  export type $Tagged<T extends ResultType> = {
    uuid: string;
    type: T;
    result: TypeToPrimitive<T>;
    sectionId?: SectionId;
    metadata?: SourceMetadata;
    hits?: HitTracking;
  };

  export type SourceMetadata = {
    source: string; // name of search source that returned the result
    latency: number;
  };

  export type TaggedResult =
    | $Tagged<ResultType.Stack>
    | $Tagged<ResultType.StackItem>
    | $Tagged<ResultType.SearchResult>
    | $Tagged<ResultType.PreviousQuery>
    | $Tagged<ResultType.SuggestedQuery>
    | $Tagged<ResultType.URLShortcut>
    | $Tagged<ResultType.Recommendation>
    | $Tagged<ResultType.MathCalculation>
    | $Tagged<ResultType.SearchFilter>
    | $Tagged<ResultType.DesktopFile>
    | $Tagged<ResultType.DesktopApplication>
    | $Tagged<ResultType.AskSuggestion>
    | $Tagged<ResultType.AskUserQuery>
    | $Tagged<ResultType.RecentChat>
    | $Tagged<ResultType.DraftWithSource>
    | $Tagged<ResultType.DraftFromTemplate>
    | $Tagged<ResultType.DraftFromTone>
    | $Tagged<ResultType.OrganizeUserQuery>
    | $Tagged<ResultType.WriteUserQuery>
    | $Tagged<ResultType.PeopleFilter>
    | $Tagged<ResultType.PeopleRecommendation>
    | $Tagged<ResultType.MyProfileSuggestion>
    | $Tagged<ResultType.CalendarEvent>;

  export const SCORING_COMPONENT_KEYS = [
    "titleMatchScore",
    "lastClickedScore",
    "frequentlyClickedScore",
    "lastBrowserViewedScore",
    "frequentlyBrowserViewedScore",
    "fileTypeScore",
    "serverScore",
  ] as const;

  export type ScoringComponentKey = (typeof SCORING_COMPONENT_KEYS)[number];

  export type Weights = {
    [key in ScoringComponentKey]: number;
  };

  /**
   * null scores are used to indicate that the score is not applicable and
   * therefore the weight of that score should be set to 0 to avoid scores
   * that don't apply to an item from dragging its score up or down
   */
  export type UnweightedScores = Record<ScoringComponentKey, number | null>;

  export type ScoringComponent = {
    unweightedScore: number | null;
    weightedScore: number | null;
    configuredWeight: number;
    appliedWeight: number | null;
  };

  export type ScoringComponents = {
    [key in ScoringComponentKey]: ScoringComponent;
  };

  export type ScoringNotes = {
    pinned: boolean;
    scoringComponents: ScoringComponents;
  };

  export type $Scored<T extends ResultType> = $Tagged<T> & {
    score: number;
    scoringNotes: ScoringNotes;
    sectionScore: number;
  };

  export type ScoredStack = $Scored<ResultType.Stack>;
  export type ScoredStackItem = $Scored<ResultType.StackItem>;
  export type ScoredSearchResult = $Scored<ResultType.SearchResult>;
  export type ScoredPreviousQuery = $Scored<ResultType.PreviousQuery>;
  export type ScoredSuggestedQuery = $Scored<ResultType.SuggestedQuery>;
  export type ScoredURLShortcut = $Scored<ResultType.URLShortcut>;
  export type ScoredRecommendation = $Scored<ResultType.Recommendation>;
  export type ScoredMathCalculation = $Scored<ResultType.MathCalculation>;
  export type ScoredSearchFilter = $Scored<ResultType.SearchFilter>;
  export type ScoredDesktopFile = $Scored<ResultType.DesktopFile>;
  export type ScoredDesktopApplication = $Scored<ResultType.DesktopApplication>;
  export type ScoredAskSuggestion = $Scored<ResultType.AskSuggestion>;
  export type ScoredAskUserQuery = $Scored<ResultType.AskUserQuery>;
  export type ScoredRecentChat = $Scored<ResultType.RecentChat>;
  export type ScoredDraftWithSource = $Scored<ResultType.DraftWithSource>;
  export type ScoredDraftFromTemplate = $Scored<ResultType.DraftFromTemplate>;
  export type ScoredDraftFromTone = $Scored<ResultType.DraftFromTone>;
  export type ScoredOrganizeUserQuery = $Scored<ResultType.OrganizeUserQuery>;
  export type ScoredWriteUserQuery = $Scored<ResultType.WriteUserQuery>;
  export type ScoredPeopleFilter = $Scored<ResultType.PeopleFilter>;
  export type ScoredPeopleRecommendation =
    $Scored<ResultType.PeopleRecommendation>;
  export type ScoredMyProfileSuggestion =
    $Scored<ResultType.MyProfileSuggestion>;
  export type ScoredCalendarEvent = $Scored<ResultType.CalendarEvent>;
  export type ScoredResult =
    | ScoredStack
    | ScoredStackItem
    | ScoredSearchResult
    | ScoredPreviousQuery
    | ScoredSuggestedQuery
    | ScoredURLShortcut
    | ScoredRecommendation
    | ScoredMathCalculation
    | ScoredSearchFilter
    | ScoredDesktopFile
    | ScoredDesktopApplication
    | ScoredAskSuggestion
    | ScoredAskUserQuery
    | ScoredRecentChat
    | ScoredOrganizeUserQuery
    | ScoredDraftWithSource
    | ScoredDraftFromTemplate
    | ScoredDraftFromTone
    | ScoredWriteUserQuery
    | ScoredPeopleFilter
    | ScoredPeopleRecommendation
    | ScoredMyProfileSuggestion
    | ScoredCalendarEvent;

  export type PredicateFunctionOptions = {
    config: Config;
    cacheAnalysis: CacheAnalysis;
    sections?: PredicateSubject<SectionId>[];
  };

  export type PredicateFunction = (
    query: string,
    mode: Mode,
    options: PredicateFunctionOptions,
  ) => boolean;

  export type TaggedSearchOptions = {
    config: Config;
    cache: TypeaheadCache;
    sections?: PredicateSubject<SectionId>[];
    grossLimit: number;
    sectionLimit: number | undefined;
  };

  export type TaggedSearch = (
    query: string,
    mode: Mode,
    options: TaggedSearchOptions,
  ) => Observable<typeahead.ScoredResult>;

  export type PredicateSubject<T = SectionId | SourceId> = {
    id: T;
    predicate: PredicateFunction;
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  } & (T extends SourceId ? { search: TaggedSearch } : {});
}

type UserQueryResult = {
  uuid: string;
  query: string;
};
export type SuggestedQuery = UserQueryResult;
export type AskUserQuery = UserQueryResult;
export type OrganizeUserQuery = UserQueryResult;
export type WriteUserQuery = UserQueryResult;

export type MathCalculation = {
  uuid: string;
  query: string;
  answer: string;
};

export type DraftFromTemplateResult = {
  uuid: string;
  // Either a pre-configured template ID or a custom template ID
  templateId: PreConfiguredTemplateID | string;
  title: string;
};

export type DraftFromToneResult = {
  uuid: string;
  // Either a pre-configured voice ID or a custom voice ID
  voiceId: PreConfiguredVoiceID | string;
  title: string;
};

export type ScoredUserQuery =
  | typeahead.ScoredAskUserQuery
  | typeahead.ScoredWriteUserQuery
  | typeahead.ScoredOrganizeUserQuery
  | typeahead.ScoredSuggestedQuery;

// Export enum for easier runtime access
export const {
  ResultType: ResultType,
  SourceId: SourceId,
  Mode: Mode,
  SectionId: SectionId,
} = typeahead;

export type PeopleSyncResult = {
  peopleRecommendationsLength: number;
  orgChartLength: number;
  timestamp: number | null;
};
